
/**
 * Types for input shape
 */
export type ElementPrivilege = {
  id: string;
  name:string;
  elementId: string | null;
  isView: boolean;
  isRead: boolean;
  isCreate: boolean;
  isUpdate: boolean;
  isDelete: boolean;
  featureId: string;
};

export type FeatureBlock = {
  featureId: string;
  elementPrivilege: ElementPrivilege[];
};

/** TRUE/FALSE for CSV */
const boolToCsv = (val: boolean): string => (val ? "TRUE" : "FALSE");

/**
 * Escape a CSV field when needed:
 * - Wrap in quotes if it contains a quote(`"`), comma, or newline
 * - Escape inner quotes by doubling them
 */
const escapeCsvField = (field: string): string => {
  const str = (field ?? "").toString();
  const needsQuoting = /[",\n]/.test(str);
  if (!needsQuoting) return str;
  return `"${str.replace(/"/g, '""')}"`;
};

/**
 * Generate a "sectioned" CSV:
 *
 * Users
 * John Doe
 * Jane Smith
 *
 * (blank line)
 *
 * Privilege 1 - View,Read,Create,Update,Delete
 * <id>,TRUE,TRUE,TRUE,FALSE,FALSE
 * ...
 *
 * (blank line)
 *
 * Privilege 2 - View,Read,Create,Update,Delete
 * ...
 *
 * Notes:
 * - If the same featureId appears multiple times, blocks are merged in the order they appear.
 * - Users is a single-column section; each name is escaped safely for CSV.
 */
export const generateSectionedCsv = (
  roleName:string,
  users: string[],
  data: FeatureBlock[]
): string => {
  const sections: string[] = [];

   // --- Role section ---
  sections.push(["Role",roleName].join(','));
  sections.push("");

  // --- Users section ---
  sections.push(escapeCsvField("Users"));

  const normalizedUsers = (users ?? [])
    .map((name) => (name ?? "").toString().trim())
    .filter((name) => name.length > 0);

  for (const name of normalizedUsers) {
    sections.push(escapeCsvField(name));
  }

  // Blank line after users (even if users is empty, we still separate sections)
  sections.push("");

  // --- Merge feature blocks that share the same featureId, preserving input order ---
  // Track first appearance order
  const featureOrder: string[] = [];
  const mergedByFeature: Record<string, ElementPrivilege[]> = {};

  for (const block of data ?? []) {
    const fid = (block?.featureId ?? "").toString();
    if (!fid) continue;

    if (!mergedByFeature[fid]) {
      mergedByFeature[fid] = [];
      featureOrder.push(fid);
    }
    // Append rows from this block (preserve order)
    const rows = Array.isArray(block.elementPrivilege) ? block.elementPrivilege : [];
    mergedByFeature[fid].push(...rows);
  }

  // --- Privilege sections ---
  featureOrder.forEach((fid, idx) => {
    const label = `Privilege ${idx + 1}`;

    // Header row
    sections.push(
      [
        escapeCsvField(label),
        "View",
        "Read",
        "Create",
        "Update",
        "Delete",
      ].join(",")
    );

    const rows = mergedByFeature[fid] ?? [];

    for (const ep of rows) {
      const firstCol = ep?.name ?? ""; // Minimal version: always use `id` as first column

      const row = [
        escapeCsvField(firstCol),
        boolToCsv(!!ep?.isView),
        boolToCsv(!!ep?.isRead),
        boolToCsv(!!ep?.isCreate),
        boolToCsv(!!ep?.isUpdate),
        boolToCsv(!!ep?.isDelete),
      ].join(",");

      sections.push(row);
    }

    // Blank line between sections
    sections.push("");
  });

  // Remove trailing blank line if present
  if (sections.length && sections[sections.length - 1] === "") {
    sections.pop();
  }

  let sectionedData =  sections.join("\n");

   const blob = new Blob([sectionedData], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${roleName}_Privileges.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
  
  return sectionedData;

};

